import { Box, styled } from '@mui/material';
import { ThemeButton } from 'DesignSystem';
import React, { FC, useMemo } from 'react';
import CountriesFilter from './CountriesFilter';
import DocumentCategoryFilter from './DocumentCategoryFilter';
import {
  EFilterType,
  IFilterComponentProps,
  TFilter,
  TFilterEvent,
  TFilterType,
  TFilterValueType,
} from './filter.types';
import OwnershipFilter from './OwnershipFilter';
import PartnerFilter from './PartnerFilter';
import RawMaterialFilter from './RawMaterialFilter';
import SitesFilter from './SitesFilter';
import SiteTypeFilter from './SiteTypeFilter';
import TimeFrameFilter from './TimeFrameFilter';

const FilterComponents: Record<TFilterType, FC<IFilterComponentProps<TFilterType>>> = {
  [EFilterType.OWNERSHIP]: OwnershipFilter,
  [EFilterType.SITE_TYPE]: SiteTypeFilter,
  [EFilterType.PARTNERS]: PartnerFilter,
  [EFilterType.RAW_MATERIALS]: RawMaterialFilter,
  [EFilterType.TIME_FRAME]: TimeFrameFilter,
  [EFilterType.DOCUMENT_CATEGORY]: DocumentCategoryFilter,
  [EFilterType.SITES]: SitesFilter,
  [EFilterType.COUNTRIES]: CountriesFilter,
};

interface IFilterProps {
  filters: TFilter[];
  onFilterChange: (event: TFilterEvent) => void;
  onFilterReset: () => void;
}

const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: 'fit-content',
  padding: theme.spacing(2),
  alignItems: 'center',
  gap: 8,
  borderRadius: 8,
  border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
  background: theme.custom.themeColors.grayScale[20],
}));

const Filter: FC<IFilterProps> = ({ filters, onFilterChange, onFilterReset }) => {
  const someFilterActive = useMemo(() => filters.some(filter => !!filter.value), [filters]);

  return (
    <FilterContainer>
      {filters.map(({ type, value, ...props }) => {
        const FilterComponent = FilterComponents[type];
        return (
          <Box key={type} width={`calc(100% / ${filters.length})`} maxWidth={450}>
            <FilterComponent
              value={value as TFilterValueType<TFilterType>}
              onChange={value => onFilterChange({ type: type as EFilterType, value })}
              {...props}
            />
          </Box>
        );
      })}
      {someFilterActive && (
        <ThemeButton color="WHITE" onClick={onFilterReset}>
          Clear filters
        </ThemeButton>
      )}
    </FilterContainer>
  );
};

export default Filter;
